import React, { useEffect, useRef, useState } from 'react'
import './report_screen.css';
import PagesTitle from '../../widgets/pages_title/pages_title'
import { domainType, formatProblemDisplay } from '../../global/http_enum';
import AppBarWidget from '../../widgets/app_bar/app_bar';
import { useDispatch, useSelector } from 'react-redux';
import { getSolutionHowToPoints, getSolutionMappedTo, getSolutionFromProblemMappedTo } from '../../functions/get_solution_to';
import { getSolutionFromProblem } from '../../functions/solution';
import { getProblemImages } from '../../functions/get_problem';
import { convertDay } from '../../functions/date_converter';
import { HideProgressAlert, showProgressAlert } from '../../functions/show_progress_page';
import { changeProjectDomainAPI } from '../../api/project';
import { updateEventDomain } from '../../redux/project/projects';
import { hundleRequestFailure } from '../../functions/hundle_request_failure';
import CheckIcon from '@mui/icons-material/Check';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import FileReaderWidget from '../../widgets/file_reader/file_reader';
import { Navigate } from 'react-router-dom';
import { showAlert } from '../../functions/show_alert';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Helper function to find the correct problem for an event
const findProblemForEvent = (project, selectedEvent, targetDomain = null) => {
    const domain = targetDomain || selectedEvent?.domain;
    if (!project?.problem || !selectedEvent?.id) {
        return project?.problem?.find(obj => obj.domain === domain) || null;
    }

    // Try to get problem ID from localStorage
    try {
        const getStorageKey = () => `eventProblems_${project?.id || 'default'}`;
        const stored = localStorage.getItem(getStorageKey());
        if (stored) {
            const eventProblemMap = new Map(JSON.parse(stored));
            const problemId = eventProblemMap.get(selectedEvent.id);

            // If we have a problem ID, find by ID first
            if (problemId) {
                const problemById = project.problem.find(obj => obj.id === problemId);
                if (problemById && (!targetDomain || problemById.domain === targetDomain)) {
                    return problemById;
                }
            }
        }
    } catch (error) {
        console.error('Failed to load event problem map:', error);
    }

    // Fallback to domain matching
    return project.problem.find(obj => obj.domain === domain) || null;
};

export default function ReportScreen() {
    const state = useSelector(state => state.projectSlice.projectSelected);
    const selectedEvent = state?.selectedEvent;
    const dispatch = useDispatch();

    // All hooks must be called before any conditional returns
    const [domain, SetDomainType] = useState(selectedEvent?.domain || '');
    const theoryObject = useRef(findProblemForEvent(state, selectedEvent));
    const [mapCenter, setMapCenter] = useState({
        lat: 20.5937,
        lng: 78.9629
    });
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef(null);
    const [problemImages, setProblemImages] = useState([]);
    const [solutionImages, setSolutionImages] = useState([]);
    const [solutionImplementationImages, setSolutionImplementationImages] = useState([]);
    const problemImagesSliderRef = useRef();
    const solutionImagesSliderRef = useRef();
    const solutionImplementationSliderRef = useRef();

    useEffect(() => {
        if (selectedEvent && state?.problem) {
            theoryObject.current = findProblemForEvent(state, selectedEvent);
            SetDomainType(selectedEvent.domain);
        }
    }, [state, selectedEvent]);

    // Get images for carousels
    useEffect(() => {
        // Update theoryObject when state or selectedEvent changes
        if (selectedEvent && state?.problem) {
            theoryObject.current = findProblemForEvent(state, selectedEvent);
        }

        const problemMappedTo = theoryObject.current?.problem_mapped_to;
        if (problemMappedTo && problemMappedTo.length > 0 && domain) {
            // Get problem images
            const problemImgs = getProblemImages(problemMappedTo, domain);
            setProblemImages(problemImgs);

            // Get solution images
            const solutionImgs = getSolutionFromProblem(problemMappedTo, domain);
            setSolutionImages(solutionImgs);

            // Get solution implementation images
            const solutionImplImgs = getSolutionFromProblemMappedTo(problemMappedTo, domain);
            setSolutionImplementationImages(solutionImplImgs);
        } else {
            setProblemImages([]);
            setSolutionImages([]);
            setSolutionImplementationImages([]);
        }
    }, [state, selectedEvent, domain]);

    const settingsSlick = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
    };

    useEffect(() => {
        const geocodeLocation = async () => {
            if (selectedEvent?.location && selectedEvent.location.trim() !== '') {
                try {
                    console.log('Geocoding location in ReportScreen:', selectedEvent.location);
                    const geocodeURL = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(selectedEvent.location)}&key=AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI`;
                    const response = await fetch(geocodeURL);
                    const data = await response.json();

                    console.log('Geocoding response in ReportScreen:', data);

                    if (data.status === 'OK' && data.results && data.results.length > 0) {
                        const location = data.results[0].geometry.location;
                        const newCenter = {
                            lat: location.lat,
                            lng: location.lng
                        };
                        console.log('Setting map center in ReportScreen to:', newCenter);
                        setMapCenter(newCenter);
                    } else {
                        console.warn('Geocoding failed or no results in ReportScreen:', data.status, data);
                        // Keep default center if geocoding fails
                    }
                } catch (error) {
                    console.error('Error geocoding location in ReportScreen:', error);
                }
            } else {
                console.log('No location found for event in ReportScreen, selectedEvent:', selectedEvent);
                // Reset to default if no location
                setMapCenter({
                    lat: 20.5937,
                    lng: 78.9629
                });
            }
        };

        // Only geocode if we have a valid location
        if (selectedEvent?.location) {
            geocodeLocation();
        }
    }, [selectedEvent]);

    // Separate effect to update map when center changes and map is loaded
    useEffect(() => {
        if (mapRef.current && mapLoaded && mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
            console.log('🔄 Updating map center via useEffect to:', mapCenter);
            try {
                mapRef.current.setCenter(mapCenter);
                mapRef.current.setZoom(12);
            } catch (error) {
                console.error('Error updating map center:', error);
            }
        }
    }, [mapCenter, mapLoaded]);

    // Now we can do conditional returns after all hooks
    if (!selectedEvent) {
        showAlert(false, 'Select an event first');
        return <Navigate to={'/event'} />
    }

    // Check if a specific problem exists for this event (using localStorage mapping)
    // Only allow report screen if a problem was created for this specific event
    const checkProblemExists = () => {
        if (!selectedEvent?.id || !state?.problem || !Array.isArray(state.problem)) {
            return false;
        }

        // Try to get problem ID from localStorage
        try {
            const getStorageKey = () => `eventProblems_${state?.id || 'default'}`;
            const stored = localStorage.getItem(getStorageKey());
            if (stored) {
                const parsed = JSON.parse(stored);
                const eventProblemMap = Array.isArray(parsed) ? new Map(parsed) : new Map(Object.entries(parsed));
                const problemId = eventProblemMap.get(selectedEvent.id);

                // If we have a problem ID, check if it exists in Redux
                if (problemId) {
                    const problemById = state.problem.find(obj => String(obj.id) === String(problemId));
                    return problemById !== null && problemById !== undefined;
                }
            }
        } catch (error) {
            console.error('Failed to load event problem map:', error);
        }

        // No specific problem found for this event
        return false;
    };

    if (!checkProblemExists()) {
        showAlert(false, 'Create Problem first');
        return <Navigate to={'/problem'} />
    }

    const changeDomain = async (domains) => {
        if (domain === domains) {
            return;
        }
        showProgressAlert();
        const response = await changeProjectDomainAPI(selectedEvent.id, domains);
        HideProgressAlert();
        if (response.statusCode < 300) {
            // Update Redux state with correct action and parameters
            // This will automatically update selectedEvent.domain in Redux state
            dispatch(updateEventDomain({ projectId: state.id, eventId: selectedEvent.id, newDomain: domains }));
            // Find problem for the new domain, but still prioritize the problem created for this event
            // Use the new domain as targetDomain parameter
            theoryObject.current = findProblemForEvent(state, selectedEvent, domains);
            SetDomainType(domains);
        } else {
            hundleRequestFailure(response);
        }
    }

    // Format date
    const eventDate = selectedEvent.date || selectedEvent.created_at;
    const formattedDate = eventDate ? convertDay(eventDate) : '';

    // Determine file types
    const youtubeLinkFromFile = selectedEvent.type === 'link' ? selectedEvent.file : null;
    const youtubeLink = selectedEvent.link || youtubeLinkFromFile;
    const isYouTubeVideo = youtubeLink && (youtubeLink.includes('youtube.com') || youtubeLink.includes('youtu.be'));

    const hasUploadedFile = selectedEvent.file &&
        selectedEvent.type &&
        selectedEvent.type !== 'link' &&
        (selectedEvent.type === 'image' || selectedEvent.type === 'video' || selectedEvent.type === 'audio');

    const mapContainerStyle = {
        width: '100%',
        height: '300px',
        borderRadius: '5px',
        position: 'relative'
    };
    return (
        <>
            <AppBarWidget />
            <div id='report-screen'>
                <div className="title-holder">
                    <PagesTitle title={'Reports'} />
                    <div className="domain-pills">
                        <button
                            className={`domain-pill ${domain === domainType.theory ? 'active' : ''}`}
                            onClick={() => changeDomain(domainType.theory)}
                        >
                            Theory Domain
                        </button>
                        <button
                            className={`domain-pill ${domain === domainType.communication ? 'active' : ''}`}
                            onClick={() => changeDomain(domainType.communication)}
                        >
                            Communication Domain
                        </button>
                    </div>
                </div>

                <div className="report-content-container">
                    {/* Left Column - Text Content */}
                    <div className="report-left-column">
                        {/* About Event Section */}
                        <div className="section-container">
                            <div className="domain-pills domain-pills-mobile">
                                <button
                                    className={`domain-pill ${domain === domainType.theory ? 'active' : ''}`}
                                    onClick={() => changeDomain(domainType.theory)}
                                >
                                    Theory Domain
                                </button>
                                <button
                                    className={`domain-pill ${domain === domainType.communication ? 'active' : ''}`}
                                    onClick={() => changeDomain(domainType.communication)}
                                >
                                    Communication Domain
                                </button>
                            </div>
                            <h2 className="section-title">About Event</h2>

                            <div className="detail-row">
                                <span className="detail-label">Event Title</span>
                                <span className="detail-value">{selectedEvent.name || ''}</span>
                            </div>

                            <div className="detail-row">
                                <span className="detail-label">Event Location</span>
                                <span className="detail-value">{selectedEvent.location || ''}</span>
                            </div>

                            <div className="detail-row">
                                <span className="detail-label">Event Date</span>
                                <span className="detail-value">{formattedDate}</span>
                            </div>

                            <div className="detail-row-full">
                                <span className="detail-label">Event Description</span>
                                <div className="detail-value description-text" dangerouslySetInnerHTML={{ __html: selectedEvent.desciption || '' }}></div>
                            </div>

                            <div className="detail-row">
                                <span className="detail-label">Problem Identified from Event</span>
                                <span className="detail-value negative">{formatProblemDisplay(selectedEvent.problem_identifyed_from_event) || ''}</span>
                            </div>

                            <div className="detail-row">
                                <span className="detail-label">Negative Function Executed</span>
                                <span className="detail-value negative">{selectedEvent.negative_function_exluded || ''}</span>
                            </div>
                        </div>

                        {/* About Problem Section */}
                        <div className="section-divider"></div>
                        <div className="section-container">
                            <h2 className="section-title">About Problem</h2>

                            {/* Only show content if problem exists for selected domain */}
                            {theoryObject.current && theoryObject.current.domain === domain ? (
                                <>
                                    <div className="detail-row-full">
                                        <span className="detail-label">Problem Mapped To:</span>
                                        {theoryObject.current && theoryObject.current.problem_mapped_to ? (
                                            <div className="checklist-items">
                                                {theoryObject.current.problem_mapped_to.map((item, index) => (
                                                    <div key={index} className="checklist-item">
                                                        <CheckIcon className="check-icon" />
                                                        <span className="checklist-text negative">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="detail-row-full">
                                        <span className="detail-label">Principle Deficiency</span>
                                        {theoryObject.current && theoryObject.current.pricipal_defeciency ? (
                                            <div className="checklist-items">
                                                {theoryObject.current.pricipal_defeciency.map((item, index) => (
                                                    <div key={index} className="checklist-item">
                                                        <CheckIcon className="check-icon" />
                                                        <span className="checklist-text blue">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <div className="detail-row-full">
                                    <span className="detail-value" style={{ color: '#999', fontStyle: 'italic' }}>
                                        No problem data available for {domain} domain
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* About Solution Section */}
                        <div className="section-divider"></div>
                        <div className="section-container">
                            <h2 className="section-title">About Solution</h2>
                            {/* Only show content if problem exists for selected domain */}
                            {theoryObject.current && theoryObject.current.domain === domain ? (
                                <>
                                    <div className="detail-row-full">
                                        <span className="detail-label">Solution Mapped To</span>
                                        {theoryObject.current ? (
                                            <div className="checklist-items">
                                                {getSolutionMappedTo(theoryObject.current.problem_mapped_to, domain).map((item, index) => (
                                                    <div key={index} className="checklist-item">
                                                        <CheckIcon className="check-icon" />
                                                        <span className="checklist-text green">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="detail-row-full">
                                        <span className="detail-label">Principle Needed</span>
                                        {theoryObject.current && theoryObject.current.pricipal_defeciency ? (
                                            <div className="checklist-items">
                                                {theoryObject.current.pricipal_defeciency.map((item, index) => (
                                                    <div key={index} className="checklist-item">
                                                        <CheckIcon className="check-icon" />
                                                        <span className="checklist-text blue">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <div className="detail-row-full">
                                    <span className="detail-value" style={{ color: '#999', fontStyle: 'italic' }}>
                                        No solution data available for {domain} domain
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* About Solution Implementation Section */}
                        <div className="section-divider"></div>
                        <div className="section-container">
                            <h2 className="section-title">About Solution Implementation</h2>
                            {/* Only show content if problem exists for selected domain */}
                            {theoryObject.current && theoryObject.current.domain === domain ? (
                                <div className="detail-row-full">
                                    <span className="detail-label">Solution How To</span>
                                    {theoryObject.current ? (
                                        <div className="checklist-items solution-how-to-items">
                                            {getSolutionHowToPoints(theoryObject.current.problem_mapped_to, domain).map((item, index) => {
                                                if (item === ' ') {
                                                    return <br key={index} />;
                                                }
                                                return (
                                                    <div key={index} className="checklist-item">
                                                        <CheckIcon className="check-icon" />
                                                        <span className="checklist-text green">{item}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="detail-row-full">
                                    <span className="detail-value" style={{ color: '#999', fontStyle: 'italic' }}>
                                        No solution implementation data available for {domain} domain
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Visual/Media Content */}
                    <div className="report-right-column">
                        {/* Event Map Section */}
                        <div className="media-section">
                            <h3 className="media-title">Event Map</h3>
                            <div className="media-content map-container" style={{ width: '100%', height: '300px', position: 'relative', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                                {selectedEvent?.location && selectedEvent.location.trim() !== '' ? (
                                    <div style={{ width: '100%', height: '300px', position: 'relative' }}>
                                        <LoadScript
                                            googleMapsApiKey="AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI"
                                            loadingElement={<div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading map...</div>}
                                        >
                                            <GoogleMap
                                                key={`map-${selectedEvent.id}-${mapCenter.lat}-${mapCenter.lng}`}
                                                mapContainerStyle={mapContainerStyle}
                                                zoom={mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 ? 12 : 2}
                                                center={mapCenter}
                                                options={{
                                                    disableDefaultUI: false,
                                                    zoomControl: true,
                                                    streetViewControl: false,
                                                    mapTypeControl: false,
                                                    fullscreenControl: false,
                                                }}
                                                onLoad={(map) => {
                                                    console.log('✅ GoogleMap loaded in ReportScreen');
                                                    console.log('📍 Map center:', mapCenter);
                                                    console.log('📍 Event location:', selectedEvent.location);
                                                    mapRef.current = map;
                                                    setMapLoaded(true);
                                                    // Always update map center when map loads if we have valid coordinates
                                                    if (mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
                                                        console.log('🔄 Setting map center on load to:', mapCenter);
                                                        setTimeout(() => {
                                                            map.setCenter(mapCenter);
                                                            map.setZoom(12);
                                                        }, 100);
                                                    }
                                                }}
                                                onError={(error) => {
                                                    console.error('❌ GoogleMap error in ReportScreen:', error);
                                                }}
                                            >
                                                {mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 && (
                                                    <Marker
                                                        position={mapCenter}
                                                        title={selectedEvent.location}
                                                        onLoad={() => console.log('✅ Marker loaded at:', mapCenter)}
                                                    />
                                                )}
                                            </GoogleMap>
                                        </LoadScript>
                                    </div>
                                ) : (
                                    <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                                        No location set for this event
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* YouTube Video Section */}
                        {isYouTubeVideo && (
                            <div className="media-section">
                                <h3 className="media-title">YouTube Video</h3>
                                <div className="media-content video-container">
                                    <FileReaderWidget file={youtubeLink} type="link" />
                                </div>
                            </div>
                        )}

                        {/* Files Section */}
                        {hasUploadedFile && (
                            <div className="media-section">
                                <h3 className="media-title">Files</h3>
                                <div className="media-content file-container">
                                    <FileReaderWidget file={selectedEvent.file} type={selectedEvent.type} />
                                </div>
                            </div>
                        )}

                        {/* Problem Images Carousel - Only show if problem exists for selected domain */}
                        {theoryObject.current && theoryObject.current.domain === domain && problemImages.length > 0 && (
                            <div className="media-section">
                                <h3 className="media-title">Problem Mapped To</h3>
                                <div className="media-content">
                                    <div className="report-carousel-container">
                                        <Slider ref={problemImagesSliderRef} className='report-carousel' {...settingsSlick}>
                                            {problemImages.map((image, index) => (
                                                <div key={index} className="report-carousel-slide">
                                                    <img alt={`Problem Mapped To ${index + 1}`} src={image} className="report-carousel-image" />
                                                </div>
                                            ))}
                                        </Slider>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Solution Images Carousel - Only show if problem exists for selected domain */}
                        {theoryObject.current && theoryObject.current.domain === domain && solutionImages.length > 0 && (
                            <div className="media-section">
                                <h3 className="media-title">Solution Mapped To</h3>
                                <div className="media-content">
                                    <div className="report-carousel-container">
                                        <Slider ref={solutionImagesSliderRef} className='report-carousel' {...settingsSlick}>
                                            {solutionImages.map((image, index) => (
                                                <div key={index} className="report-carousel-slide">
                                                    <img alt={`Solution Mapped To ${index + 1}`} src={image} className="report-carousel-image" />
                                                </div>
                                            ))}
                                        </Slider>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Solution Implementation Images Carousel - Only show if problem exists for selected domain */}
                        {theoryObject.current && theoryObject.current.domain === domain && solutionImplementationImages.length > 0 && (
                            <div className="media-section">
                                <h3 className="media-title">Solution How To</h3>
                                <div className="media-content">
                                    <div className="report-carousel-container">
                                        <Slider ref={solutionImplementationSliderRef} className='report-carousel' {...settingsSlick}>
                                            {solutionImplementationImages.map((image, index) => (
                                                <div key={index} className="report-carousel-slide">
                                                    <img alt={`Solution How To ${index + 1}`} src={image} className="report-carousel-image" />
                                                </div>
                                            ))}
                                        </Slider>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
